/**
 * Kollisionshinweis und Avatar-Stack (Spec 7.1).
 *
 * Zwei Stufen, weil zwei Dinge unterschiedlich dringend sind:
 *   - **„liest gerade"** ist eine Randnotiz → nur der Avatar-Stack.
 *   - **„bearbeitet gerade"** ist eine Warnung → sichtbares Banner, denn hier
 *     entsteht gleich eine zweite Antwort an denselben Kunden.
 *
 * Der eigene Eintrag faellt heraus: dass man selbst am Ticket sitzt, weiss man.
 */
import type { PresenceEntry } from '@tickethub/shared';
import { Eye, PencilLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InitialsAvatar } from '@/components/InitialsAvatar';
import { Alert, AlertDescription } from '@/components/ui/alert';

type PresenceBannerProps = {
  entries: PresenceEntry[];
  /** Eigene Mitglieds-ID — der eigene Eintrag wird ausgeblendet. */
  ownMemberId: string | null;
};

export function PresenceBanner({ entries, ownMemberId }: PresenceBannerProps) {
  const { t } = useTranslation();
  const others = entries.filter((entry) => entry.member_id !== ownMemberId);
  if (others.length === 0) return null;

  const editing = others.filter((entry) => entry.editing);
  const typing = others.filter((entry) => entry.typing);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2" aria-label={t('presence.title')}>
        <ul className="flex -space-x-2">
          {others.map((entry) => (
            <li key={entry.member_id}>
              <InitialsAvatar
                name={entry.name}
                className="size-7 border-2 border-background text-[10px]"
              />
            </li>
          ))}
        </ul>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Eye aria-hidden="true" className="size-3" />
          {t('presence.viewing', { count: others.length })}
        </span>
      </div>

      {editing.length > 0 ? (
        <Alert>
          <AlertDescription className="flex items-center gap-2">
            <PencilLine aria-hidden="true" className="size-4" />
            {typing.length > 0
              ? t('presence.typing', { name: typing.map((entry) => entry.name).join(', ') })
              : t('presence.editing', { name: editing.map((entry) => entry.name).join(', ') })}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
